import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CHAT_RATING_LABELS, CHAT_RATING_PERIODS, type ChatRatingPeriod, type ChatRatingStats } from '@kontor/shared';
import { api } from '../../api';

const PERIOD_LABELS: Record<ChatRatingPeriod, string> = { '30': 'Letzte 30 Tage', '90': 'Letzte 90 Tage', '365': 'Letzte 12 Monate' };
const decimal = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 2 });
const percent = new Intl.NumberFormat('de-DE', { style: 'percent', maximumFractionDigits: 0 });
const day = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' });

/** Sterne als Text, für Bildschirmleser mit Zahl */
export function Stars({ value }: { value: number }) {
  const full = Math.round(value);
  return (
    <span className="stars-text" role="img" aria-label={`${decimal.format(value)} von 5 Sternen`}>
      {'★'.repeat(full)}
      <span className="stars-text__off">{'★'.repeat(5 - full)}</span>
    </span>
  );
}

/**
 * Auswertung der Bewertungen aus dem Webchat (KON-174): Wie zufrieden waren die Besucher, wie verteilen sich die Sterne, wer hat wie abgeschnitten und was haben die
 * Besucher dazu geschrieben. Grundlage für die Qualität der Beratung.
 */
export function RatingsPanel({ onOpen }: { onOpen: (id: string) => void }) {
  const [period, setPeriod] = useState<ChatRatingPeriod>('30');
  const stats = useQuery({ queryKey: ['webchat', 'ratings', period], queryFn: async () => (await api<{ data: ChatRatingStats }>('GET', `/webchat/ratings?period=${period}`)).data });
  const s = stats.data;
  const top = s ? Math.max(1, ...s.distribution) : 1;

  return (
    <section className="card chat-ratings" aria-label="Auswertung der Bewertungen">
      <div className="chat-ratings__head">
        <h2>Bewertungen der Besucher</h2>
        <label className="chat-ratings__period">
          <span className="muted small">Zeitraum</span>
          <select className="input" value={period} onChange={(e) => setPeriod(e.target.value as ChatRatingPeriod)}>
            {CHAT_RATING_PERIODS.map((p) => (
              <option key={p} value={p}>{PERIOD_LABELS[p]}</option>
            ))}
          </select>
        </label>
      </div>
      {stats.isLoading && <p className="muted">Wird geladen …</p>}
      {stats.isError && <div className="alert alert--error" role="alert">{(stats.error as Error).message}</div>}
      {s && s.rated === 0 && <p className="muted chat-empty">In diesem Zeitraum hat noch kein Besucher bewertet. Bei {s.chats} Unterhaltungen ist das noch keine Aussage über die Qualität.</p>}
      {s && s.rated > 0 && (
        <>
          <div className="kpis chat-ratings__kpis">
            <div className="kpi">
              <span className="kpi__value">{s.average === null ? '–' : decimal.format(s.average)}</span>
              <span className="kpi__label">Durchschnitt von 5 Sternen</span>
              {s.average !== null && <Stars value={s.average} />}
            </div>
            <div className="kpi">
              <span className="kpi__value">{s.rated}</span>
              <span className="kpi__label">Bewertungen</span>
            </div>
            <div className="kpi">
              <span className="kpi__value">{s.chats > 0 ? percent.format(Math.min(1, s.rated / s.chats)) : '–'}</span>
              <span className="kpi__label">Rücklauf bei {s.chats} Unterhaltungen</span>
            </div>
          </div>

          <h3>Verteilung</h3>
          <ul className="chat-dist" aria-label="Anzahl je Sternezahl">
            {[5, 4, 3, 2, 1].map((n) => {
              const count = s.distribution[n - 1]!;
              return (
                <li key={n}>
                  <span className="chat-dist__label">{n} {n === 1 ? 'Stern' : 'Sterne'}<span className="sr-only">, {CHAT_RATING_LABELS[n]}</span></span>
                  <span className="chat-dist__bar" aria-hidden="true"><span style={{ width: `${(count / top) * 100}%` }} /></span>
                  <span className="chat-dist__count">{count}</span>
                </li>
              );
            })}
          </ul>

          <h3>Je Mitarbeiter</h3>
          <p className="muted small">Gezählt wird der Mitarbeiter, dem die Unterhaltung zugewiesen war. Wer antwortet, übernimmt sie, wenn sie noch niemandem gehört.</p>
          <table className="table table--cards">
            <thead>
              <tr><th>Mitarbeiter</th><th>Bewertungen</th><th>Durchschnitt</th></tr>
            </thead>
            <tbody>
              {s.byUser.map((u) => (
                <tr key={u.userId ?? 'none'}>
                  <td data-label="Mitarbeiter">{u.name ?? <span className="muted">Ohne Zuständigen</span>}</td>
                  <td data-label="Bewertungen">{u.rated}</td>
                  <td data-label="Durchschnitt"><Stars value={u.average} /> <span className="muted small">{decimal.format(u.average)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>

          {s.comments.length > 0 && (
            <>
              <h3>Neueste Kommentare</h3>
              <ul className="chat-comments">
                {s.comments.map((c) => (
                  <li key={c.id}>
                    <button type="button" className="chat-comment" onClick={() => onOpen(c.id)}>
                      <span className="chat-comment__top"><Stars value={c.rating} /> <span className="muted small">{c.visitorName ?? 'Besucher'} · {day.format(new Date(c.ratedAt))}</span></span>
                      <span className="chat-comment__text">{c.comment}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </section>
  );
}
